import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, X, ArrowRight } from 'lucide-react';
import { PRODUCTS, COLLECTIONS } from '../mock';

const PRICE_MIN = 0;
const PRICE_MAX = 20000;

const Search = () => {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get('q') || '';
  const [query, setQuery] = useState(initialQ);
  const [priceMin, setPriceMin] = useState(PRICE_MIN);
  const [priceMax, setPriceMax] = useState(PRICE_MAX);
  const [activeBrand, setActiveBrand] = useState('All');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    if (query) setParams({ q: query }, { replace: true });
    else setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const brands = useMemo(() => ['All', ...Array.from(new Set(PRODUCTS.map((p) => p.brand)))].sort(), []);
  const categories = useMemo(() => ['All', ...Array.from(new Set(PRODUCTS.map((p) => p.category)))].sort(), []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Tokenise so multi-word queries like "carbon hood mazda" match
    // products regardless of word order.
    const tokens = q ? q.split(/\s+/).filter(Boolean) : [];
    return PRODUCTS.filter((p) => {
      if (tokens.length) {
        const collection = COLLECTIONS.find((c) => c.id === p.collectionId);
        const veh = p.vehicle || {};
        const haystack = [
          p.name,
          p.brand,
          p.category,
          p.sku,
          p.slug,
          collection?.name,
          veh.make,
          veh.modelName,
          veh.years,
          (p.tags || []).join(' '),
          (p.description || '').replace(/<[^>]+>/g, ' '),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        // Every search token must appear somewhere in the haystack.
        if (!tokens.every((t) => haystack.includes(t))) return false;
      }
      if (p.price < priceMin || p.price > priceMax) return false;
      if (activeBrand !== 'All' && p.brand !== activeBrand) return false;
      if (activeCategory !== 'All' && p.category !== activeCategory) return false;
      return true;
    });
  }, [query, priceMin, priceMax, activeBrand, activeCategory]);

  const reset = () => {
    setQuery('');
    setPriceMin(PRICE_MIN);
    setPriceMax(PRICE_MAX);
    setActiveBrand('All');
    setActiveCategory('All');
  };

  return (
    <div className="bg-white text-neutral-900 min-h-screen">
      <section className="pt-36 md:pt-32 pb-10 border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-2">SEARCH</div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tight mb-6">FIND YOUR PARTS</h1>
          <div className="relative max-w-3xl">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products by name, brand, vehicle or category..."
              className="w-full bg-neutral-50 border-2 border-neutral-200 pl-12 pr-12 py-4 text-lg focus:outline-none focus:border-[#FF6B00]"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
                <X size={20} />
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
          {/* Filters */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display tracking-[0.22em] text-sm font-bold">FILTERS</h2>
              <button onClick={reset} className="text-xs text-[#FF6B00] hover:underline font-display tracking-[0.18em]">RESET</button>
            </div>

            <div className="mb-8">
              <div className="font-display tracking-[0.2em] text-[11px] text-neutral-500 mb-3">PRICE RANGE</div>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1">
                  <label className="block text-[10px] text-neutral-400 mb-1">MIN</label>
                  <input
                    type="number"
                    value={priceMin}
                    min={PRICE_MIN}
                    max={priceMax}
                    onChange={(e) => setPriceMin(Math.max(PRICE_MIN, Math.min(priceMax, Number(e.target.value) || 0)))}
                    className="w-full bg-white border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B00]"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[10px] text-neutral-400 mb-1">MAX</label>
                  <input
                    type="number"
                    value={priceMax}
                    min={priceMin}
                    max={PRICE_MAX}
                    onChange={(e) => setPriceMax(Math.min(PRICE_MAX, Math.max(priceMin, Number(e.target.value) || 0)))}
                    className="w-full bg-white border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B00]"
                  />
                </div>
              </div>
              <div className="relative h-1 bg-neutral-200 mt-4">
                <div
                  className="absolute h-1 bg-[#FF6B00]"
                  style={{
                    left: `${(priceMin / PRICE_MAX) * 100}%`,
                    right: `${100 - (priceMax / PRICE_MAX) * 100}%`,
                  }}
                />
              </div>
              <div className="flex justify-between text-[11px] text-neutral-400 mt-2">
                <span>${priceMin.toLocaleString()}</span>
                <span>${priceMax.toLocaleString()}+</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { label: 'Under $500', min: 0, max: 499 },
                  { label: '$500\u2013$1K', min: 500, max: 999 },
                  { label: '$1K\u2013$2K', min: 1000, max: 1999 },
                  { label: '$2K+', min: 2000, max: PRICE_MAX },
                ].map((p) => (
                  <button
                    key={p.label}
                    onClick={() => { setPriceMin(p.min); setPriceMax(p.max); }}
                    className="text-[11px] font-display tracking-[0.18em] border border-neutral-300 px-2.5 py-1.5 hover:border-[#FF6B00] hover:text-[#FF6B00]"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <div className="font-display tracking-[0.2em] text-[11px] text-neutral-500 mb-3">BRAND</div>
              <div className="flex flex-wrap gap-2">
                {brands.map((b) => (
                  <button
                    key={b}
                    onClick={() => setActiveBrand(b)}
                    className={`text-[11px] font-display tracking-[0.18em] px-3 py-1.5 border ${
                      activeBrand === b ? 'bg-[#FF6B00] border-[#FF6B00] text-white' : 'border-neutral-300 hover:border-neutral-900'
                    }`}
                  >
                    {b.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="font-display tracking-[0.2em] text-[11px] text-neutral-500 mb-3">CATEGORY</div>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setActiveCategory(c)}
                    className={`text-[11px] font-display tracking-[0.18em] px-3 py-1.5 border ${
                      activeCategory === c ? 'bg-[#FF6B00] border-[#FF6B00] text-white' : 'border-neutral-300 hover:border-neutral-900'
                    }`}
                  >
                    {c.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Results */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-neutral-600">
                <span className="font-display tracking-[0.18em] text-neutral-900">{results.length}</span> result{results.length !== 1 ? 's' : ''}
                {query && <> for <span className="font-display tracking-[0.18em] text-[#FF6B00]">&ldquo;{query}&rdquo;</span></>}
              </div>
            </div>

            {results.length === 0 ? (
              <div className="py-20 text-center text-neutral-500">
                <p className="text-lg mb-3">No products match your filters.</p>
                <button onClick={reset} className="text-[#FF6B00] hover:underline font-display tracking-[0.18em] text-sm">RESET FILTERS</button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {results.slice(0, 60).map((p) => (
                  <Link key={p.id} to={`/product/${p.slug}`} className="group block">
                    <div className="aspect-square bg-neutral-100 overflow-hidden mb-4">
                      <img src={p.images[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display tracking-[0.2em] text-[10px] text-[#FF6B00] font-semibold mb-1">{p.brand.toUpperCase()} &middot; {p.category.toUpperCase()}</div>
                        <h3 className="font-display text-base tracking-wide leading-tight group-hover:text-[#FF6B00] transition-colors line-clamp-2">{p.name}</h3>
                        <div className="text-neutral-600 text-sm mt-1">${p.price.toLocaleString()} USD</div>
                      </div>
                      <ArrowRight size={18} className="text-neutral-400 group-hover:text-[#FF6B00] group-hover:translate-x-1 transition-all mt-1 shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
            {results.length > 60 && (
              <div className="mt-10 text-center text-sm text-neutral-500">Showing first 60 of {results.length}. Refine filters to narrow down.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Search;
